import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Paper,
  Avatar,
  Card,
  CardContent,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Badge
} from '@mui/material';
import {
  Person as PersonIcon,
  Edit as EditIcon,
  History as HistoryIcon,
  Security as SecurityIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  Assignment as AssignmentIcon,
  Visibility as VisibilityIcon,
  PhotoCamera as PhotoCameraIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { FIELD_LIMITS } from '../../constants/fieldLimits';

const PerfilesUsuarios = () => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Mock data for demonstration
  const mockUsers = [
    {
      id: 1,
      username: 'juan.perez',
      email: 'juan.perez@conem.com',
      firstName: 'Juan',
      firstLastName: 'Pérez',
      secondLastName: 'García',
      role: 'admin',
      isActive: true,
      avatar: null,
      phone: '+52 55 1234-5678',
      department: 'Administración',
      position: 'Gerente General',
      hireDate: '2023-01-15',
      lastLogin: '2024-08-24 14:30:00',
      quotationsCreated: 45,
      reportsGenerated: 23,
      permissions: ['create_quotations', 'manage_users', 'view_reports', 'system_config'],
      activityHistory: [
        { date: '2024-08-24 14:30:00', action: 'Inicio de sesión', details: 'Acceso desde IP 192.168.1.100' },
        { date: '2024-08-24 13:45:00', action: 'Cotización creada', details: 'COT-20240824001' },
        { date: '2024-08-24 12:15:00', action: 'Usuario editado', details: 'Modificó perfil de María González' },
        { date: '2024-08-24 11:30:00', action: 'Reporte generado', details: 'Reporte mensual de agosto' }
      ]
    },
    {
      id: 2,
      username: 'maria.gonzalez',
      email: 'maria.gonzalez@conem.com',
      firstName: 'María',
      firstLastName: 'González',
      secondLastName: 'López',
      role: 'user',
      isActive: true,
      avatar: null,
      phone: '+52 55 9876-5432',
      department: 'Ventas',
      position: 'Ejecutiva de Ventas',
      hireDate: '2023-03-20',
      lastLogin: '2024-08-24 12:15:00',
      quotationsCreated: 78,
      reportsGenerated: 12,
      permissions: ['create_quotations', 'view_reports'],
      activityHistory: [
        { date: '2024-08-24 12:15:00', action: 'Inicio de sesión', details: 'Acceso desde IP 192.168.1.105' },
        { date: '2024-08-24 11:45:00', action: 'Cotización creada', details: 'COT-20240824002' },
        { date: '2024-08-24 10:30:00', action: 'Cotización modificada', details: 'COT-20240823015' }
      ]
    }
  ];

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Simular carga de usuarios
      setTimeout(() => {
        setUsers(mockUsers);
        setLoading(false);
      }, 1000);
    } catch (err) {
      setError('Error al cargar los perfiles de usuarios');
      setLoading(false);
    }
  };

  const handleViewProfile = (user) => {
    setSelectedUser(user);
    setProfileDialogOpen(true);
    setEditMode(false);
    setActiveTab(0);
  };

  const handleEditProfile = () => {
    setEditMode(true);
  };

  const handleSaveProfile = async () => {
    try {
      setError('');
      // Simular guardado
      setTimeout(() => {
        setSuccess('Perfil actualizado exitosamente');
        setEditMode(false);
        loadUsers();
      }, 1000);
    } catch (err) {
      setError('Error al actualizar el perfil');
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const getRoleColor = (role) => {
    return role === 'admin' ? 'error' : 'primary';
  };

  const getStatusColor = (isActive) => {
    return isActive ? 'success' : 'default';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{
      width: '95%',
      maxWidth: '1400px',
      mx: 'auto',
      mt: 2,
      mb: 2,
      background: '#fff',
      borderRadius: 4,
      boxShadow: '0 4px 32px rgba(0,0,0,0.10)',
      p: 4,
    }}>
      {/* Header */}
      <Box sx={{ position: 'relative', mb: 4 }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 700,
            letterSpacing: 1,
            color: '#222',
            fontFamily: "'Fira Mono', 'Menlo', 'Monaco', 'Consolas', 'monospace'",
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&::before': {
              content: '"👤"',
              marginRight: 2,
              fontSize: '1.2em'
            }
          }}
        >
          Perfiles de Usuarios Detallados
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      {/* Users Grid */}
      <Grid container spacing={3}>
        {users.map((user) => (
          <Grid size={{ xs: 12, md: 6, lg: 4 }} key={user.id}>
            <Card sx={{ 
              height: '100%',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
              }
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    badgeContent={
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: user.isActive ? '#4caf50' : '#f44336',
                          border: '2px solid white'
                        }}
                      />
                    }
                  >
                    <Avatar
                      sx={{ 
                        width: 64, 
                        height: 64, 
                        mr: 2,
                        backgroundColor: user.role === 'admin' ? '#f44336' : '#1976d2'
                      }}
                    >
                      {user.firstName[0]}{user.firstLastName[0]}
                    </Avatar>
                  </Badge>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" fontWeight="bold">
                      {user.firstName} {user.firstLastName}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      @{user.username}
                    </Typography>
                    <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                      <Chip 
                        label={user.role} 
                        color={getRoleColor(user.role)} 
                        size="small" 
                        variant="outlined"
                      />
                      <Chip 
                        label={user.isActive ? 'Activo' : 'Inactivo'} 
                        color={getStatusColor(user.isActive)} 
                        size="small"
                      />
                    </Box>
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <EmailIcon sx={{ fontSize: 16, mr: 1 }} />
                    {user.email}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <BusinessIcon sx={{ fontSize: 16, mr: 1 }} />
                    {user.department} - {user.position}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ display: 'flex', alignItems: 'center' }}>
                    <CalendarIcon sx={{ fontSize: 16, mr: 1 }} />
                    Último acceso: {new Date(user.lastLogin).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" color="primary">
                      {user.quotationsCreated}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Cotizaciones
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" color="success.main">
                      {user.reportsGenerated}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Reportes
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" color="info.main">
                      {user.permissions.length}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Permisos
                    </Typography>
                  </Box>
                </Box>

                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<VisibilityIcon />}
                  onClick={() => handleViewProfile(user)}
                  sx={{ fontWeight: 600, textTransform: 'none' }}
                >
                  Ver Perfil Completo
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Profile Detail Dialog */}
      <Dialog
        open={profileDialogOpen}
        onClose={() => setProfileDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            maxHeight: '90vh'
          }
        }}
      >
        {selectedUser && (
          <>
            <DialogTitle sx={{ 
              backgroundColor: '#f8f9fa', 
              fontWeight: 700, 
              fontSize: '1.3rem',
              color: '#2c3e50',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PersonIcon sx={{ mr: 2 }} />
                Perfil de {selectedUser.firstName} {selectedUser.firstLastName}
              </Box>
              {!editMode && (
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={handleEditProfile}
                  sx={{ fontWeight: 600, textTransform: 'none' }}
                >
                  Editar
                </Button>
              )}
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
              <Tabs 
                value={activeTab} 
                onChange={handleTabChange} 
                sx={{ borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab label="Información Personal" />
                <Tab label="Permisos y Roles" />
                <Tab label="Historial de Actividad" />
              </Tabs>

              <Box sx={{ p: 3 }}>
                {/* Tab 0: Personal Information */}
                {activeTab === 0 && (
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Box sx={{ textAlign: 'center', mb: 3 }}>
                        <Avatar
                          sx={{ 
                            width: 120, 
                            height: 120, 
                            mx: 'auto', 
                            mb: 2,
                            backgroundColor: selectedUser.role === 'admin' ? '#f44336' : '#1976d2'
                          }}
                        >
                          {selectedUser.firstName[0]}{selectedUser.firstLastName[0]}
                        </Avatar>
                        {editMode && (
                          <Button
                            variant="outlined"
                            startIcon={<PhotoCameraIcon />}
                            size="small"
                            sx={{ fontWeight: 600, textTransform: 'none' }}
                          >
                            Cambiar Foto
                          </Button>
                        )}
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 8 }}>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            fullWidth
                            label="Nombre"
                            value={selectedUser.firstName}
                            disabled={!editMode}
                            variant={editMode ? "outlined" : "filled"}
                  inputProps={{ maxLength: FIELD_LIMITS.personName }} />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            fullWidth
                            label="Apellido Paterno"
                            value={selectedUser.firstLastName}
                            disabled={!editMode}
                            variant={editMode ? "outlined" : "filled"}
                  inputProps={{ maxLength: FIELD_LIMITS.personName }} />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            fullWidth
                            label="Apellido Materno"
                            value={selectedUser.secondLastName}
                            disabled={!editMode}
                            variant={editMode ? "outlined" : "filled"}
                  inputProps={{ maxLength: FIELD_LIMITS.personName }} />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            fullWidth
                            label="Usuario"
                            value={selectedUser.username}
                            disabled={!editMode}
                            variant={editMode ? "outlined" : "filled"}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            fullWidth
                            label="Email"
                            value={selectedUser.email}
                            disabled={!editMode}
                            variant={editMode ? "outlined" : "filled"}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            fullWidth
                            label="Teléfono"
                            value={selectedUser.phone}
                            disabled={!editMode}
                            variant={editMode ? "outlined" : "filled"}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            fullWidth
                            label="Departamento"
                            value={selectedUser.department}
                            disabled={!editMode}
                            variant={editMode ? "outlined" : "filled"}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            fullWidth
                            label="Posición"
                            value={selectedUser.position}
                            disabled={!editMode}
                            variant={editMode ? "outlined" : "filled"}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            fullWidth
                            label="Fecha de Contratación"
                            value={selectedUser.hireDate}
                            disabled={!editMode}
                            variant={editMode ? "outlined" : "filled"}
                            type="date"
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            fullWidth
                            label="Último Acceso"
                            value={new Date(selectedUser.lastLogin).toLocaleString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            disabled
                            variant="filled"
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                )}

                {/* Tab 1: Permissions and Roles */}
                {activeTab === 1 && (
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                            <SecurityIcon sx={{ mr: 1 }} />
                            Rol y Estado
                          </Typography>
                          <Box sx={{ mb: 2 }}>
                            <Chip 
                              label={selectedUser.role} 
                              color={getRoleColor(selectedUser.role)} 
                              size="large"
                              sx={{ mr: 1 }}
                            />
                            <Chip 
                              label={selectedUser.isActive ? 'Activo' : 'Inactivo'} 
                              color={getStatusColor(selectedUser.isActive)} 
                              size="large"
                            />
                          </Box>
                          <Typography variant="body2" color="textSecondary">
                            El usuario tiene {selectedUser.permissions.length} permisos asignados
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            Estadísticas de Actividad
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="h4" color="primary">
                                {selectedUser.quotationsCreated}
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                Cotizaciones Creadas
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="h4" color="success.main">
                                {selectedUser.reportsGenerated}
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                Reportes Generados
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            Permisos Asignados
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {selectedUser.permissions.map((permission, index) => (
                              <Chip
                                key={index}
                                label={permission.replace('_', ' ').toUpperCase()}
                                color="info"
                                variant="outlined"
                                size="small"
                              />
                            ))}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                )}

                {/* Tab 2: Activity History */}
                {activeTab === 2 && (
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                        <HistoryIcon sx={{ mr: 1 }} />
                        Historial de Actividad Reciente
                      </Typography>
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Fecha y Hora</TableCell>
                              <TableCell>Acción</TableCell>
                              <TableCell>Detalles</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selectedUser.activityHistory.map((activity, index) => (
                              <TableRow key={index}>
                                <TableCell>
                                  {new Date(activity.date).toLocaleString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                </TableCell>
                                <TableCell>
                                  <Chip 
                                    label={activity.action} 
                                    size="small" 
                                    color="primary" 
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell>{activity.details}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                )}
              </Box>
            </DialogContent>

            <DialogActions sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
              {editMode ? (
                <>
                  <Button 
                    onClick={() => setEditMode(false)}
                    startIcon={<CancelIcon />}
                    sx={{ fontWeight: 600, textTransform: 'none' }}
                  >
                    Cancelar
                  </Button>
                  <Button 
                    onClick={handleSaveProfile}
                    variant="contained"
                    startIcon={<SaveIcon />}
                    sx={{ fontWeight: 600, textTransform: 'none' }}
                  >
                    Guardar Cambios
                  </Button>
                </>
              ) : (
                <Button 
                  onClick={() => setProfileDialogOpen(false)}
                  sx={{ fontWeight: 600, textTransform: 'none' }}
                >
                  Cerrar
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default PerfilesUsuarios;
